import type { CirclesChart } from '@shared/circlesChart';
import type { CirclesEngineState } from '@shared/engine/circles/state';
import { comboMultiplier } from '@shared/scoring';
import { COMBO_MILESTONE_THRESHOLDS } from './circlesRenderConstants';
import { HpBarRenderer } from './circlesDrawHpBar';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Live HUD renderer — owns the per-frame animation state for the
 * score readout, combo counter, multiplier badge, judgement tallies,
 * progress strip, and song title. The HP bar is delegated to
 * {@link HpBarRenderer} so each module stays focused on a single
 * HUD region.
 *
 * Each animation pulse is a frame-rate-independent decay seeded by a
 * state transition detected in {@link tickAnimations} (e.g. `state.score`
 * went up → `scoreBumpT = 1`, then decays toward 0).
 *
 * All state here is render-only. The engine never sees the combo
 * milestone pulse or the broken-combo ghost — they exist purely so
 * the HUD can communicate "what just happened" with motion alongside
 * the raw numbers.
 */
export class HudRenderer {
  /**
   * Frame-to-frame transitions in the engine state drive HUD pulses.
   * `lastShownX` is the value rendered on the previous frame — a
   * mismatch with the engine's current value seeds an animation.
   */
  private lastShownCombo = 0;
  private comboPulseT = 0;
  private comboMilestonePulseT = 0;
  private comboBreakT = 0;
  private comboBreakValue = 0;
  private lastShownScore = 0;
  private scoreBumpT = 0;
  private reducedMotion: boolean;
  private quality: RenderQuality;
  private hpBar: HpBarRenderer;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    this.hpBar = new HpBarRenderer(reducedMotion, quality);
  }

  /** Forward HP-tracking reset to the bar renderer on loop wrap. */
  clearHpTracking(): void {
    this.hpBar.clearTracking();
  }

  /** Render-harness hook — forwards to the bar renderer. See {@link HpBarRenderer.testOnlyPrimeHpForHarness}. */
  testOnlyPrimeHpForHarness(priorHp: number): void {
    this.hpBar.testOnlyPrimeHpForHarness(priorHp);
  }

  draw(
    ctx: CanvasRenderingContext2D,
    state: CirclesEngineState,
    beat: number,
    chart: CirclesChart,
    w: number,
    animDt: number,
    displayedScore: number,
  ): void {
    this.tickAnimations(state, animDt);
    this.hpBar.tick(state, animDt);
    ctx.save();

    // ── Top-left HUD column ──
    // Layout (anchored at canvas origin, padded inward 24px):
    //   y =  38   "SCORE" eyebrow label (10px uppercase tracking)
    //   y =  78   live score readout (52px bold, scale-bumps on gain)
    //   y = 110   running combo (e.g. "37x", 36px bold, milestone pulse) —
    //              hidden when combo=0 so the empty state isn't loud
    //   y = 134   multiplier badge (e.g. "x1.25", 14px bold,
    //              colour-tiered by tier so the eye reads "what
    //              multiplier am I on" at a glance)
    //   y = 158   judgement tallies (11px mono, dim grey)
    //
    // Score + combo carry the dominant visual weight here. They are
    // the player's "this run matters" signal, so they get scoreboard-
    // grade type sizes and stack vertically on the left edge clear of
    // the centred HP bar and the right-edge song title.
    const HUD_LEFT_X = 24;

    ctx.font = '10px "Rajdhani", system-ui, sans-serif';
    ctx.fillStyle = 'rgba(232, 236, 255, 0.55)';
    ctx.shadowBlur = 0;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    // Tracking via letter-spacing isn't supported on Canvas2D; we
    // approximate by setting the font and accepting natural spacing —
    // the all-caps short label reads clearly without forced kerning.
    ctx.fillText('SCORE', HUD_LEFT_X, 38);

    // Score readout: bump-on-increment scale that grows in place from
    // the left baseline anchor. The displayed value is the session's
    // eased score so count-up + visual bump fire together.
    const scoreScale = 1 + this.scoreBumpT * 0.18;
    ctx.save();
    ctx.translate(HUD_LEFT_X, 78);
    ctx.scale(scoreScale, scoreScale);
    ctx.font = 'bold 52px "Rajdhani", "Orbitron", system-ui, sans-serif';
    ctx.fillStyle = '#fff';
    if (this.quality.chromeShadows) {
      ctx.shadowColor = '#16e0ff';
      ctx.shadowBlur = 14 + this.scoreBumpT * 20;
    }
    ctx.fillText(Math.floor(displayedScore).toLocaleString(), 0, 0);
    ctx.restore();

    const mult = comboMultiplier(state.combo);
    const comboBaseColour =
      mult >= 2
        ? '#ffd84d'
        : mult >= 1.5
          ? '#ff2bd6'
          : mult >= 1.25
            ? '#16e0ff'
            : state.combo > 0
              ? '#e8ecff'
              : '#8e96c7';
    const comboBaseShadow = mult >= 2 ? 28 : mult >= 1.5 ? 22 : mult >= 1.25 ? 14 : 6;

    // Running combo (only when > 0) — 36px bold with a milestone pulse
    // overlay. Anchor at the same left edge so growth radiates
    // rightward, never dragging back over the score above.
    if (state.combo > 0) {
      const comboScale =
        1 + this.comboPulseT * 0.14 + this.comboMilestonePulseT * 0.28;
      ctx.save();
      ctx.translate(HUD_LEFT_X, 110);
      ctx.scale(comboScale, comboScale);
      ctx.font = 'bold 36px "Rajdhani", "Orbitron", system-ui, sans-serif';
      ctx.fillStyle = comboBaseColour;
      if (this.quality.chromeShadows) {
        ctx.shadowColor = comboBaseColour;
        ctx.shadowBlur = comboBaseShadow + this.comboMilestonePulseT * 18;
      }
      ctx.fillText(`${state.combo}x`, 0, 0);
      ctx.restore();
    }

    // Multiplier badge: small persistent indicator below the combo
    // number so the player always knows their score multiplier even
    // before combo > 0. Tier colour matches `comboBaseColour` for
    // visual cohesion.
    ctx.font = 'bold 14px "Rajdhani", system-ui, sans-serif';
    ctx.fillStyle = comboBaseColour;
    if (this.quality.chromeShadows) {
      ctx.shadowColor = comboBaseColour;
      ctx.shadowBlur = comboBaseShadow;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.fillText(`x${mult}`, HUD_LEFT_X, 134);

    // Crumble: when a combo breaks, render a fading red ghost of the
    // previous count drifting downward + tilting on top of where the
    // big combo number used to sit. Reads as "your combo just shattered".
    if (this.comboBreakT > 0 && this.comboBreakValue > 0) {
      const ghostT = 1 - this.comboBreakT;
      const ghostY = 110 + ghostT * 36;
      const ghostAlpha = this.comboBreakT * this.comboBreakT;
      const ghostRot = ghostT * 0.18;
      ctx.save();
      ctx.globalAlpha = ghostAlpha;
      ctx.translate(HUD_LEFT_X, ghostY);
      ctx.rotate(ghostRot);
      ctx.font = 'bold 28px "Rajdhani", system-ui, sans-serif';
      ctx.fillStyle = '#ff3b5c';
      if (this.quality.chromeShadows) {
        ctx.shadowColor = '#ff3b5c';
        ctx.shadowBlur = 18;
      }
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(`${this.comboBreakValue}x BROKEN`, 0, 0);
      ctx.restore();
    }

    ctx.font = '11px monospace';
    ctx.fillStyle = '#8e96c7';
    ctx.shadowBlur = 0;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    // OK count is omitted from the live HUD until a player actually
    // lands one — most clean runs never touch the mercy band, so
    // showing "OK 0" forever would be visual clutter for nothing.
    // Use the full "OK" abbreviation (not "O") to avoid reading as
    // a zero in the monospace HUD font.
    const okSegment = state.oks > 0 ? `  OK${state.oks}` : '';
    ctx.fillText(
      `P${state.perfects}  G${state.goods}${okSegment}  M${state.misses}`,
      HUD_LEFT_X,
      158,
    );

    ctx.textAlign = 'right';
    ctx.font = 'bold 14px monospace';
    ctx.fillStyle = '#fff';
    if (this.quality.chromeShadows) {
      ctx.shadowColor = '#16e0ff';
      ctx.shadowBlur = 8;
    }
    const progress = Math.max(0, Math.min(1, beat / chart.totalBeats));
    ctx.fillText(`${Math.round(progress * 100)}%`, w - 16, 28);
    ctx.font = '11px monospace';
    ctx.fillStyle = '#8e96c7';
    ctx.shadowBlur = 0;
    ctx.fillText(chart.title.toUpperCase(), w - 16, 46);

    const barW = 160;
    const barH = 4;
    const barX = w - 16 - barW;
    const barY = 54;
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(barX, barY, barW, barH);
    ctx.fillStyle = chart.palette.pad;
    ctx.fillRect(barX, barY, barW * progress, barH);

    this.hpBar.draw(ctx, state, w);
    ctx.restore();
  }

  /**
   * Detect frame-to-frame transitions in score/combo and trigger HUD
   * animation pulses. Decays previously-active pulses toward zero with
   * frame-rate-independent rates. HP-bar animation state is owned by
   * {@link HpBarRenderer} and ticked separately.
   *
   * Reduced-motion users get pulse magnitudes zeroed out — the
   * animations skip entirely rather than playing at half-strength,
   * since "tasteful" still means "extra motion" by definition.
   */
  private tickAnimations(state: CirclesEngineState, animDt: number): void {
    if (state.combo > this.lastShownCombo) {
      this.comboPulseT = 1;
      // Milestone hits stack a second, larger pulse so the moment
      // reads as "this combo specifically just hit a tier".
      let isMilestone = false;
      for (let i = 0; i < COMBO_MILESTONE_THRESHOLDS.length; i++) {
        if (state.combo === COMBO_MILESTONE_THRESHOLDS[i]) {
          isMilestone = true;
          break;
        }
      }
      if (isMilestone) {
        this.comboMilestonePulseT = 1;
      }
    } else if (state.combo === 0 && this.lastShownCombo > 0) {
      this.comboBreakValue = this.lastShownCombo;
      this.comboBreakT = 1;
    }
    this.lastShownCombo = state.combo;

    if (state.score > this.lastShownScore) {
      this.scoreBumpT = 1;
    }
    this.lastShownScore = state.score;

    if (this.reducedMotion) {
      this.comboPulseT = 0;
      this.comboMilestonePulseT = 0;
      this.comboBreakT = 0;
      this.scoreBumpT = 0;
      return;
    }

    this.comboPulseT = Math.max(0, this.comboPulseT - animDt * 5);
    this.comboMilestonePulseT = Math.max(0, this.comboMilestonePulseT - animDt * 3);
    this.comboBreakT = Math.max(0, this.comboBreakT - animDt * 3);
    this.scoreBumpT = Math.max(0, this.scoreBumpT - animDt * 6);
  }
}
